/**
 * UI sound effects for the whole storefront — **old-school Mario (NES) style**.
 *
 * No audio files are downloaded: every sound is synthesised with the Web Audio
 * API using **square waves** (the NES chip) and short noise hits, so it is
 * instant, offline-safe and cannot 404. Sounds are built from the classic Mario
 * notes: the coin (B5 → E6), the 1-UP jingle, the power-up run, the block bump,
 * the fireball, the pipe and the pause blip.
 *
 * Usage:
 *   playSfx('coin')             // or read the data-sfx attribute (global handler)
 *   <button data-sfx="success">…</button>   // plays the 1-UP jingle instead
 *   <button data-sfx="none">…</button>      // silent
 *
 * The global handler in `components/SoundEffects.js` plays the coin for every
 * button / link, so no component has to do anything. The user's on/off choice is
 * remembered in localStorage (`ms_sfx`, on by default).
 */
const STORE_KEY = 'ms_sfx';

/** Notes of the NES chip (A4 = 440 Hz) — the keys are used by the sounds below. */
const NOTES = {
  G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
  C6: 1046.50, D6: 1174.66, E6: 1318.51, F6: 1396.91, G6: 1567.98, A6: 1760.00, B6: 1975.53,
  C7: 2093.00, D7: 2349.32, E7: 2637.02, F7: 2793.83, G7: 3135.98, A7: 3520.00, B7: 3951.07,
  C8: 4186.01,
};

/** 'E6' → 1318.51 Hz (a plain number is used as-is). */
const note = (name) => (typeof name === 'number' ? name : (NOTES[name] || 440));

let ctx = null;
let master = null;

export function isSfxOn() {
  try {
    return localStorage.getItem(STORE_KEY) !== '0';   // default: on
  } catch (e) {
    return true;
  }
}

export function setSfxOn(on) {
  try {
    localStorage.setItem(STORE_KEY, on ? '1' : '0');
  } catch (e) { /* private mode — keep it in memory only */ }
  if (on) playSfx('toggle-on');
}

export function toggleSfx() {
  const next = !isSfxOn();
  setSfxOn(next);
  return next;
}

/** Lazily create the AudioContext (browsers require a user gesture first). */
function ensureContext() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.13;              // overall volume: 8-bit square waves are loud
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
  return ctx;
}

/** A single chip tone with a smooth envelope (no clicks/pops). */
function tone({ freq = 660, to = 0, type = 'square', at = 0, dur = 0.09, gain = 1 }) {
  const ac = ensureContext();
  if (!ac) return;
  const t0 = ac.currentTime + at;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;                                   // 'square' = the NES chip
  osc.frequency.setValueAtTime(freq, t0);
  if (to && to !== freq) osc.frequency.exponentialRampToValueAtTime(Math.max(40, to), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);           // fast attack
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);           // natural decay
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.03);
}

/**
 * Play a chip-tune melody — old-school Mario sounds are just short runs of
 * square notes, e.g. the 1-UP jingle:
 *   seq([['E6', 0.075], ['G6', 0.075], ['E7', 0.075], ['C7', 0.075], ['D7', 0.075], ['G7', 0.24]]);
 * `null` as a note name is a rest.
 */
function seq(steps, { gain = 0.4, type = 'square' } = {}) {
  let at = 0;
  steps.forEach(([name, dur]) => {
    if (name) tone({ freq: note(name), at, dur, gain, type });
    at += dur;
  });
}

/** A filtered noise "tick" used to give the click some texture. */
function tick({ at = 0, dur = 0.035, freq = 2600, q = 0.8, gain = 0.5 }) {
  const ac = ensureContext();
  if (!ac) return;
  const t0 = ac.currentTime + at;
  const frames = Math.max(1, Math.floor(ac.sampleRate * dur));
  const buffer = ac.createBuffer(1, frames, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames);          // decaying noise
  }
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const g = ac.createGain();
  g.gain.value = gain;
  src.connect(filter).connect(g).connect(master);
  src.start(t0);
}

const SOUNDS = {
  // ── classic Mario building blocks (also usable as data-sfx="coin" …) ──
  // the coin: B5 → E6, the most famous two notes of the NES
  coin: () => {
    tone({ freq: note('B5'), dur: 0.052, gain: 0.30 });
    tone({ freq: note('E6'), at: 0.052, dur: 0.13, gain: 0.26 });
  },
  // jumping: a square sweep upwards
  jump: () => tone({ freq: 300, to: 980, dur: 0.2, gain: 0.32 }),
  // menu / cursor blip
  blip: () => {
    tone({ freq: note('C6'), dur: 0.035, gain: 0.26 });
    tone({ freq: note('G6'), at: 0.035, dur: 0.05, gain: 0.22 });
  },
  // hitting a block from below
  bump: () => {
    tone({ freq: 250, to: 150, dur: 0.08, gain: 0.3 });
    tick({ freq: 800, gain: 0.16 });
  },
  // squashing a goomba
  stomp: () => {
    tick({ freq: 900, gain: 0.28 });
    tone({ freq: 320, to: 130, dur: 0.1, gain: 0.3 });
  },
  // shooting a fireball
  fireball: () => {
    tone({ freq: 1300, to: 320, dur: 0.16, gain: 0.3 });
    tick({ freq: 2000, gain: 0.18 });
  },
  // going down a pipe
  pipe: () => tone({ freq: 700, to: 220, dur: 0.24, gain: 0.3 }),
  // the 1-UP jingle
  oneUp: () => seq([['E6', 0.075], ['G6', 0.075], ['E7', 0.075], ['C7', 0.075],
    ['D7', 0.075], ['G7', 0.24]], { gain: 0.36 }),
  // a power-up appears (mushroom / fire flower)
  powerUp: () => seq([['G4', 0.045], ['C5', 0.045], ['E5', 0.045], ['G5', 0.045],
    ['C6', 0.045], ['E6', 0.045], ['G6', 0.045], ['C7', 0.17]], { gain: 0.34 }),
  // losing the power-up (shrinking)
  powerDown: () => seq([['C7', 0.05], ['G6', 0.05], ['E6', 0.05], ['C6', 0.05],
    ['G5', 0.05], ['E5', 0.05], ['C5', 0.05], ['G4', 0.18]], { gain: 0.32 }),
  // the pause blip
  pauseBlip: () => seq([['B5', 0.05], ['E5', 0.05], [null, 0.02], ['C5', 0.12]], { gain: 0.3 }),

  // ── the sounds the UI asks for (every button already plays one) ──
  click: () => SOUNDS.coin(),                       // every button / link
  tap: () => seq([['C6', 0.035], ['E6', 0.05]], { gain: 0.24 }),   // links, tabs
  toggle: () => SOUNDS.pauseBlip(),                 // switches (dark mode, sound, language…)
  'toggle-on': () => SOUNDS.powerUp(),              // sound / dark mode turned on
  'toggle-off': () => SOUNDS.powerDown(),           // …turned off
  success: () => SOUNDS.oneUp(),                    // like, follow, publish, copied…
  error: () => seq([['B5', 0.08], ['F5', 0.08], ['F5', 0.08], ['E5', 0.3]], { gain: 0.3 }),  // Mario's tumble
  send: () => SOUNDS.fireball(),                    // message / voice note sent
  record: () => SOUNDS.pipe(),                      // start / stop recording
  play: () => SOUNDS.jump(),                        // music player starts
  pause: () => seq([['B5', 0.05], ['F5', 0.13]], { gain: 0.28 }),
  notify: () => seq([['E6', 0.06], ['G6', 0.06], ['E7', 0.06], ['B7', 0.2]], { gain: 0.32 }),
  pop: () => seq([['C5', 0.04], ['E5', 0.04], ['G5', 0.04], ['C6', 0.11]], { gain: 0.3 }),
};

/** Play one sound by name (silently ignored when sounds are off / unsupported). */
export function playSfx(name = 'click') {
  if (!isSfxOn()) return;
  const fn = SOUNDS[name] || SOUNDS.click;
  try {
    fn();
  } catch (e) { /* never break the UI because of a sound */ }
}

/** Warm up the AudioContext on the first real gesture (avoids the first delay). */
export function primeSfx() {
  try { ensureContext(); } catch (e) { /* ignore */ }
}

export const SFX_NAMES = Object.keys(SOUNDS);
